/**
 * A5 用户详情聚合页（Tab 切分）
 *
 * Stream 2 Phase 2-4b / Plan §10 Stream 2 / Design §4.2 A5.
 *
 * 功能：
 *  - 路由参数 userId（useParams）
 *  - 顶部 PageHeader：标题 "用户 {nickname}({userId})"，副标题 status Tag + counts 概览
 *  - 返回按钮 <Link to="/users">
 *  - 用户基础信息 Card（所有字段；phone/unionId 后端已处理脱敏）
 *  - 冻结/解冻操作区（条件渲染）
 *  - Tabs：作品 / 订单 / 讲述记录 / 助力关系 / 分享 / 权益
 *
 * 整页 PermissionGate USER_READ。
 */
import { useState } from 'react';
import {
  Card,
  Tag,
  Button,
  Space,
  Result,
  Spin,
  Typography,
  Alert,
  Tabs,
  Table,
  Modal,
  Form,
  Input,
  message,
  Descriptions,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../../components/PermissionGate';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  getUser,
  getUserWorks,
  getUserOrders,
  getUserDialogues,
  getUserBoost,
  getUserShares,
  getUserVouchers,
  freezeUser,
  unfreezeUser,
  type UserStatus,
  type WorkItem,
  type UserOrderItem,
  type DialogueItem,
  type BoostRel,
  type BoostProg,
  type ShareItem,
  type VoucherItem,
  type CouponItem,
} from '../../../api/users';

const { Text, Title } = Typography;
const { TextArea } = Input;

const PII_PLACEHOLDER_PREFIX = '[已脱敏';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function userStatusColor(status: UserStatus): string {
  switch (status) {
    case 'active':    return 'green';
    case 'frozen':    return 'red';
    case 'recycling': return 'gold';
    case 'deleted':   return 'default';
    default:          return 'default';
  }
}

function userStatusLabel(status: UserStatus): string {
  switch (status) {
    case 'active':    return '正常';
    case 'frozen':    return '已冻结';
    case 'recycling': return '回收中';
    case 'deleted':   return '已删除';
    default:          return status;
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// ---------------------------------------------------------------------------
// Tab: 作品
// ---------------------------------------------------------------------------

function WorksTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const query = useQuery({
    queryKey: ['user-works', userId, page, pageSize],
    queryFn: () => getUserWorks(userId, { page, pageSize }),
  });

  const columns: ColumnsType<WorkItem> = [
    { title: '作品 ID', dataIndex: 'workId', key: 'workId' },
    { title: '标题', dataIndex: 'title', key: 'title' },
    {
      title: '状态',
      dataIndex: 'lifecycleStatus',
      key: 'lifecycleStatus',
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: '书籍解锁',
      dataIndex: 'isBookUnlocked',
      key: 'isBookUnlocked',
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'default'}>{v ? '已解锁' : '未解锁'}</Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: unknown, row: WorkItem) => (
        <Tooltip title="等 Stream 3 接入">
          <Button size="small" disabled data-testid={`work-link-disabled-${row.workId}`}>
            查看详情
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Table<WorkItem>
      rowKey="workId"
      columns={columns}
      dataSource={query.data?.items ?? []}
      loading={query.isLoading}
      pagination={{
        current: page,
        pageSize,
        total: query.data?.total ?? 0,
        showSizeChanger: true,
        showTotal: (t) => `共 ${t} 条`,
        onChange: (p, ps) => { setPage(p); setPageSize(ps); },
      }}
      data-testid="works-tab-table"
    />
  );
}

// ---------------------------------------------------------------------------
// Tab: 订单
// ---------------------------------------------------------------------------

function OrdersTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const query = useQuery({
    queryKey: ['user-orders', userId, page, pageSize],
    queryFn: () => getUserOrders(userId, { page, pageSize }),
  });

  const columns: ColumnsType<UserOrderItem> = [
    {
      title: '订单 ID',
      dataIndex: 'orderId',
      key: 'orderId',
      render: (id: string) => (
        <Link to={`/orders/${id}`} data-testid={`order-link-${id}`}>
          {id}
        </Link>
      ),
    },
    { title: '作品 ID', dataIndex: 'workId', key: 'workId' },
    {
      title: '金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (v: number) => `¥${(v / 100).toFixed(2)}`,
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: '支付状态',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => formatDateTime(v),
    },
  ];

  return (
    <Table<UserOrderItem>
      rowKey="orderId"
      columns={columns}
      dataSource={query.data?.items ?? []}
      loading={query.isLoading}
      pagination={{
        current: page,
        pageSize,
        total: query.data?.total ?? 0,
        showSizeChanger: true,
        showTotal: (t) => `共 ${t} 条`,
        onChange: (p, ps) => { setPage(p); setPageSize(ps); },
      }}
      data-testid="orders-tab-table"
    />
  );
}

// ---------------------------------------------------------------------------
// Tab: 讲述记录
// ---------------------------------------------------------------------------

function DialoguesTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const query = useQuery({
    queryKey: ['user-dialogues', userId, page, pageSize],
    queryFn: () => getUserDialogues(userId, { page, pageSize }),
  });

  const columns: ColumnsType<DialogueItem> = [
    { title: '对话 ID', dataIndex: 'dialogueId', key: 'dialogueId' },
    { title: '作品 ID', dataIndex: 'workId', key: 'workId' },
    {
      title: '开始时间',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '结束时间',
      dataIndex: 'endedAt',
      key: 'endedAt',
      render: (v: string | null) => formatDateTime(v),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag>{s}</Tag>,
    },
    { title: '话语数', dataIndex: 'utteranceCount', key: 'utteranceCount' },
  ];

  return (
    <Table<DialogueItem>
      rowKey="dialogueId"
      columns={columns}
      dataSource={query.data?.items ?? []}
      loading={query.isLoading}
      expandable={{
        expandedRowRender: (record: DialogueItem) => {
          const isPiiMasked =
            record.utterancesPreview.length > 0 &&
            record.utterancesPreview[0].text.startsWith(PII_PLACEHOLDER_PREFIX);

          return (
            <div data-testid={`dialogue-preview-${record.dialogueId}`}>
              {isPiiMasked && (
                <Alert
                  type="warning"
                  showIcon
                  message="需 pii:read_content 权限才可查看对话内容"
                  style={{ marginBottom: 8 }}
                  data-testid="pii-content-warning"
                />
              )}
              {record.utterancesPreview.map((u) => (
                <div
                  key={u.utteranceId}
                  style={{ marginBottom: 4, display: 'flex', gap: 8 }}
                  data-testid={`utterance-${u.utteranceId}`}
                >
                  <Tag color={u.role === 'user' ? 'blue' : 'green'}>
                    {u.role === 'user' ? '用户' : 'AI'}
                  </Tag>
                  <Text>{u.text}</Text>
                </div>
              ))}
              {record.utterancesPreview.length === 0 && (
                <Text type="secondary">暂无预览</Text>
              )}
            </div>
          );
        },
      }}
      pagination={{
        current: page,
        pageSize,
        total: query.data?.total ?? 0,
        showSizeChanger: true,
        showTotal: (t) => `共 ${t} 条`,
        onChange: (p, ps) => { setPage(p); setPageSize(ps); },
      }}
      data-testid="dialogues-tab-table"
    />
  );
}

// ---------------------------------------------------------------------------
// Tab: 助力关系
// ---------------------------------------------------------------------------

function BoostTab({ userId }: { userId: string }) {
  const query = useQuery({
    queryKey: ['user-boost', userId],
    queryFn: () => getUserBoost(userId),
  });

  const relColumns: ColumnsType<BoostRel> = [
    { title: '关系 ID', dataIndex: 'relationId', key: 'relationId' },
    { title: '邀请人 ID', dataIndex: 'inviterId', key: 'inviterId' },
    { title: '被邀请人 ID', dataIndex: 'inviteeId', key: 'inviteeId' },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => formatDateTime(v),
    },
  ];

  const progColumns: ColumnsType<BoostProg> = [
    { title: '进度 ID', dataIndex: 'progressId', key: 'progressId' },
    { title: '用户 ID', dataIndex: 'userId', key: 'userId' },
    { title: '阶段', dataIndex: 'stage', key: 'stage' },
    {
      title: '完成时间',
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (v: string | null) => formatDateTime(v),
    },
  ];

  return (
    <PermissionGate
      point={PERMISSIONS.BOOST_READ_RELATIONSHIPS}
      fallback={
        <Alert
          type="warning"
          showIcon
          message="需 boost:read_relationships 权限"
          data-testid="boost-tab-no-perm"
        />
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card title="助力关系" size="small">
          <Table<BoostRel>
            rowKey="relationId"
            columns={relColumns}
            dataSource={query.data?.relationships ?? []}
            loading={query.isLoading}
            pagination={false}
            data-testid="boost-rel-table"
          />
        </Card>
        <Card title="助力进度" size="small">
          <Table<BoostProg>
            rowKey="progressId"
            columns={progColumns}
            dataSource={query.data?.progress ?? []}
            loading={query.isLoading}
            pagination={false}
            data-testid="boost-prog-table"
          />
        </Card>
      </Space>
    </PermissionGate>
  );
}

// ---------------------------------------------------------------------------
// Tab: 分享
// ---------------------------------------------------------------------------

function SharesTab({ userId }: { userId: string }) {
  const query = useQuery({
    queryKey: ['user-shares', userId],
    queryFn: () => getUserShares(userId),
  });

  const columns: ColumnsType<ShareItem> = [
    { title: '分享 ID', dataIndex: 'shareId', key: 'shareId' },
    { title: '作品 ID', dataIndex: 'workId', key: 'workId' },
    {
      title: '分享时间',
      dataIndex: 'sharedAt',
      key: 'sharedAt',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '撤销时间',
      dataIndex: 'revokedAt',
      key: 'revokedAt',
      render: (v: string | null) => formatDateTime(v),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag>{s}</Tag>,
    },
  ];

  return (
    <PermissionGate
      point={PERMISSIONS.SHARE_READ}
      fallback={
        <Alert
          type="warning"
          showIcon
          message="需 share:read 权限"
          data-testid="shares-tab-no-perm"
        />
      }
    >
      <Table<ShareItem>
        rowKey="shareId"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isLoading}
        pagination={false}
        data-testid="shares-tab-table"
      />
    </PermissionGate>
  );
}

// ---------------------------------------------------------------------------
// Tab: 权益
// ---------------------------------------------------------------------------

function VouchersTab({ userId }: { userId: string }) {
  const query = useQuery({
    queryKey: ['user-vouchers', userId],
    queryFn: () => getUserVouchers(userId),
  });

  const voucherColumns: ColumnsType<VoucherItem> = [
    { title: '权益 ID', dataIndex: 'voucherId', key: 'voucherId' },
    { title: '类型', dataIndex: 'type', key: 'type' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: '授权时间',
      dataIndex: 'grantedAt',
      key: 'grantedAt',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '到期时间',
      dataIndex: 'expireAt',
      key: 'expireAt',
      render: (v: string | null) => formatDateTime(v),
    },
  ];

  const couponColumns: ColumnsType<CouponItem> = [
    { title: '优惠券 ID', dataIndex: 'couponId', key: 'couponId' },
    { title: '码', dataIndex: 'code', key: 'code' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: '使用时间',
      dataIndex: 'usedAt',
      key: 'usedAt',
      render: (v: string | null) => formatDateTime(v),
    },
    {
      title: '到期时间',
      dataIndex: 'expireAt',
      key: 'expireAt',
      render: (v: string | null) => formatDateTime(v),
    },
  ];

  return (
    <PermissionGate
      point={PERMISSIONS.VOUCHER_READ}
      fallback={
        <Alert
          type="warning"
          showIcon
          message="需 voucher:read 权限"
          data-testid="vouchers-tab-no-perm"
        />
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card title="权益券" size="small">
          <Table<VoucherItem>
            rowKey="voucherId"
            columns={voucherColumns}
            dataSource={query.data?.vouchers ?? []}
            loading={query.isLoading}
            pagination={false}
            data-testid="vouchers-tab-table"
          />
        </Card>
        <Card title="优惠券" size="small">
          <Table<CouponItem>
            rowKey="couponId"
            columns={couponColumns}
            dataSource={query.data?.coupons ?? []}
            loading={query.isLoading}
            pagination={false}
            data-testid="coupons-tab-table"
          />
        </Card>
      </Space>
    </PermissionGate>
  );
}

// ---------------------------------------------------------------------------
// Freeze Modal
// ---------------------------------------------------------------------------

interface FreezeModalProps {
  open: boolean;
  userId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function FreezeModal({ open, userId, onClose, onSuccess }: FreezeModalProps) {
  const [form] = Form.useForm<{ reason: string }>();

  const mutation = useMutation({
    mutationFn: (vals: { reason: string }) => freezeUser(userId, { reason: vals.reason }),
    onSuccess: () => {
      message.success('用户已冻结');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '冻结失败');
    },
  });

  return (
    <Modal
      title="冻结用户"
      open={open}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={() => {
        form.validateFields().then((vals) => mutation.mutate(vals)).catch(() => undefined);
      }}
      confirmLoading={mutation.isPending}
      okText="确认冻结"
      okButtonProps={{ danger: true }}
      data-testid="freeze-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="reason"
          label="冻结原因（5-200 字符，将记入审计日志）"
          rules={[
            { required: true, message: '请填写冻结原因' },
            { min: 5, message: '最少 5 个字符' },
            { max: 200, message: '最多 200 个字符' },
          ]}
        >
          <TextArea
            rows={4}
            placeholder="请填写冻结原因（5-200 字符）"
            data-testid="freeze-reason-input"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Unfreeze Modal
// ---------------------------------------------------------------------------

interface UnfreezeModalProps {
  open: boolean;
  userId: string;
  freezeId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function UnfreezeModal({ open, userId, freezeId, onClose, onSuccess }: UnfreezeModalProps) {
  const [form] = Form.useForm<{ reason: string }>();

  const mutation = useMutation({
    mutationFn: (vals: { reason: string }) =>
      unfreezeUser(userId, { freezeId, reason: vals.reason }),
    onSuccess: () => {
      message.success('用户已解冻');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '解冻失败');
    },
  });

  return (
    <Modal
      title="解冻用户"
      open={open}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={() => {
        form.validateFields().then((vals) => mutation.mutate(vals)).catch(() => undefined);
      }}
      confirmLoading={mutation.isPending}
      okText="确认解冻"
      data-testid="unfreeze-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="reason"
          label="解冻原因（5-200 字符，将记入审计日志）"
          rules={[
            { required: true, message: '请填写解冻原因' },
            { min: 5, message: '最少 5 个字符' },
            { max: 200, message: '最多 200 个字符' },
          ]}
        >
          <TextArea
            rows={4}
            placeholder="请填写解冻原因（5-200 字符）"
            data-testid="unfreeze-reason-input"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Main detail page (inner, after permission check)
// ---------------------------------------------------------------------------

function UsersDetailPageInner() {
  const { userId } = useParams<{ userId: string }>();
  const qc = useQueryClient();
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [unfreezeOpen, setUnfreezeOpen] = useState(false);

  const userQuery = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUser(userId!),
    enabled: !!userId,
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['user', userId] });
  }

  if (userQuery.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (userQuery.isError || !userQuery.data) {
    return (
      <Result
        status="error"
        title="加载失败"
        subTitle="用户详情获取失败，请刷新重试。"
        extra={<Button onClick={() => userQuery.refetch()}>重试</Button>}
      />
    );
  }

  const { user, counts, activeFreeze } = userQuery.data;

  const tabItems = [
    {
      key: 'works',
      label: `作品（${counts.worksCount}）`,
      children: <WorksTab userId={userId!} />,
    },
    {
      key: 'orders',
      label: `订单（${counts.ordersCount}）`,
      children: <OrdersTab userId={userId!} />,
    },
    {
      key: 'dialogues',
      label: `讲述记录（${counts.dialoguesCount}）`,
      children: <DialoguesTab userId={userId!} />,
    },
    {
      key: 'boost',
      label: '助力关系',
      children: <BoostTab userId={userId!} />,
    },
    {
      key: 'shares',
      label: `分享（${counts.sharesCount}）`,
      children: <SharesTab userId={userId!} />,
    },
    {
      key: 'vouchers',
      label: `权益（${counts.vouchersCount}）`,
      children: <VouchersTab userId={userId!} />,
    },
  ];

  return (
    <div data-testid="user-detail-page" style={{ padding: '0 0 40px' }}>
      {/* 顶部 Header */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Link to="/users">
          <Button data-testid="back-to-users">← 返回列表</Button>
        </Link>
        <Title level={4} style={{ margin: 0 }}>
          用户 {user.nickname}（{user.userId}）
        </Title>
        <Tag color={userStatusColor(user.status)} data-testid="user-status-tag">
          {userStatusLabel(user.status)}
        </Tag>
        <Text type="secondary" data-testid="user-counts-summary">
          作品 {counts.worksCount} | 订单 {counts.ordersCount} | 权益 {counts.vouchersCount} | 分享 {counts.sharesCount} | 讲述 {counts.dialoguesCount}
        </Text>
      </div>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* 用户基础信息 Card */}
        <Card title="用户基础信息" size="small" data-testid="user-basic-card">
          <Descriptions column={2} size="small">
            <Descriptions.Item label="用户 ID">{user.userId}</Descriptions.Item>
            <Descriptions.Item label="昵称">{user.nickname}</Descriptions.Item>
            <Descriptions.Item label="手机号">{user.phone}</Descriptions.Item>
            <Descriptions.Item label="unionId">{user.unionId}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={userStatusColor(user.status)}>{userStatusLabel(user.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="注册时间">{formatDateTime(user.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{formatDateTime(user.updatedAt)}</Descriptions.Item>
            <Descriptions.Item label="回收到期">
              {formatDateTime(user.recycleExpireAt)}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 冻结/解冻操作区 */}
        <Card title="账号操作" size="small" data-testid="user-action-card">
          {activeFreeze !== null ? (
            // 已冻结 → 显示冻结信息 + 解冻按钮
            <div data-testid="freeze-status-section">
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
                message={
                  <span>
                    用户已冻结：{activeFreeze.reason}，by {activeFreeze.frozenBy}，
                    可撤销至 {formatDateTime(activeFreeze.canRevertUntil)}
                  </span>
                }
                data-testid="freeze-info-alert"
              />
              <PermissionGate point={PERMISSIONS.USER_FREEZE}>
                <Button
                  danger
                  onClick={() => setUnfreezeOpen(true)}
                  data-testid="unfreeze-btn"
                >
                  解冻用户
                </Button>
              </PermissionGate>
            </div>
          ) : user.status === 'active' ? (
            // 正常 → 显示冻结按钮
            <div data-testid="freeze-action-section">
              <PermissionGate point={PERMISSIONS.USER_FREEZE}>
                <Button
                  danger
                  onClick={() => setFreezeOpen(true)}
                  data-testid="freeze-btn"
                >
                  冻结用户
                </Button>
              </PermissionGate>
            </div>
          ) : (
            <Text type="secondary" data-testid="no-action-hint">
              当前状态（{userStatusLabel(user.status)}）无可用操作
            </Text>
          )}
        </Card>

        {/* 关联资源 Tabs */}
        <Card size="small" data-testid="user-tabs-card">
          <Tabs
            defaultActiveKey="works"
            items={tabItems}
            data-testid="user-detail-tabs"
          />
        </Card>
      </Space>

      {/* 冻结 Modal */}
      <FreezeModal
        open={freezeOpen}
        userId={userId!}
        onClose={() => setFreezeOpen(false)}
        onSuccess={invalidate}
      />

      {/* 解冻 Modal */}
      {activeFreeze !== null && (
        <UnfreezeModal
          open={unfreezeOpen}
          userId={userId!}
          freezeId={activeFreeze.freezeId}
          onClose={() => setUnfreezeOpen(false)}
          onSuccess={invalidate}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export: page-level PermissionGate (USER_READ)
// ---------------------------------------------------------------------------

export default function UsersDetailPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.USER_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="您没有访问用户详情的权限，请联系管理员申请 user:read 权限。"
          data-testid="user-detail-403"
        />
      }
    >
      <UsersDetailPageInner />
    </PermissionGate>
  );
}
